import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Scale, Shield, CheckCircle } from "lucide-react";

// Terms of Service Content
const TermsContent = () => (
  <div className="space-y-6 text-sm">
    <p className="text-muted-foreground">
      Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
    </p>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">1. Acceptance of Terms</h3>
      <p className="text-muted-foreground">
        By accessing or using Bar Songz ("Service"), you agree to be bound by these Terms of 
        Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">2. Description of Service</h3>
      <p className="text-muted-foreground">
        Bar Songz provides educational audio content and materials related to law school 
        studies. Our Service is intended for educational purposes only and does not constitute 
        legal advice.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">3. User Accounts</h3>
      <p className="text-muted-foreground">
        To access certain features, you must create an account. You agree to:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Provide accurate and complete information</li>
        <li>Maintain the security of your account credentials</li>
        <li>Notify us immediately of any unauthorized access</li>
        <li>Accept responsibility for all activities under your account</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">4. Subscription and Payments</h3>
      <p className="text-muted-foreground">
        Some features require a paid subscription. By subscribing, you agree to:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Pay the applicable fees as described at time of purchase</li>
        <li>Automatic renewal unless cancelled before the renewal date</li>
        <li>No refunds for partial subscription periods</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">5. Intellectual Property</h3>
      <p className="text-muted-foreground">
        All content, including audio recordings, text, graphics, and software, is owned by 
        Bar Songz or its licensors. You may not copy, distribute, or create derivative works 
        without explicit permission.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">6. Prohibited Conduct</h3>
      <p className="text-muted-foreground">You agree not to:</p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Share your account credentials with others</li>
        <li>Download, copy, or redistribute content</li>
        <li>Use the Service for any illegal purpose</li>
        <li>Attempt to circumvent any security measures</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">7. Disclaimer</h3>
      <p className="text-muted-foreground">
        The Service is provided "as is" without warranties of any kind. We do not guarantee 
        that use of the Service will result in any particular academic outcome.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">8. Contact</h3>
      <p className="text-muted-foreground">
        For questions about these Terms, please contact us at support@barsongz.com.
      </p>
    </section>
  </div>
);

// Privacy Policy Content
const PrivacyContent = () => (
  <div className="space-y-6 text-sm">
    <p className="text-muted-foreground">
      Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
    </p>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">1. Information We Collect</h3>
      <p className="text-muted-foreground">We collect information you provide directly:</p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Account information (name, email, password)</li>
        <li>Profile information (date of birth, address)</li>
        <li>Payment information (processed securely via Stripe)</li>
        <li>Usage data (content viewed, features used)</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">2. How We Use Your Information</h3>
      <p className="text-muted-foreground">We use your information to:</p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Provide and improve our Service</li>
        <li>Process payments and manage subscriptions</li>
        <li>Send important updates about your account</li>
        <li>Personalize your learning experience</li>
        <li>Comply with legal obligations</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">3. Information Sharing</h3>
      <p className="text-muted-foreground">
        We do not sell your personal information. We may share information with:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Service providers who assist our operations</li>
        <li>Legal authorities when required by law</li>
        <li>Business partners with your consent</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">4. Data Security</h3>
      <p className="text-muted-foreground">
        We implement appropriate security measures to protect your information. However, 
        no method of transmission over the Internet is 100% secure.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">5. Your Rights</h3>
      <p className="text-muted-foreground">You have the right to:</p>
      <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Delete your account and associated data</li>
        <li>Opt out of marketing communications</li>
      </ul>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">6. Cookies</h3>
      <p className="text-muted-foreground">
        We use cookies and similar technologies to enhance your experience, analyze usage, 
        and assist in our marketing efforts. You can manage cookie preferences in your browser.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">7. Children's Privacy</h3>
      <p className="text-muted-foreground">
        Our Service is not intended for children under 13. We do not knowingly collect 
        information from children under 13.
      </p>
    </section>

    <section className="space-y-3">
      <h3 className="font-semibold text-gold">8. Contact Us</h3>
      <p className="text-muted-foreground">
        For privacy-related questions, please contact us at privacy@barsongz.com.
      </p>
    </section>
  </div>
);

export default function TermsModal({ 
  open, 
  onOpenChange, 
  type = "terms", // "terms" or "privacy"
  onAccept 
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Reset scroll state when modal opens
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
    }
  }, [open]);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    // Check if scrolled to bottom (with small buffer)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    onAccept?.();
    onOpenChange(false);
  };

  const isTerms = type === "terms";
  const title = isTerms ? "Terms of Service" : "Privacy Policy";
  const Icon = isTerms ? Scale : Shield;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" data-testid={`${type}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif">
            <Icon className="h-5 w-5 text-gold" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div 
          className="flex-1 pr-4 max-h-[50vh] overflow-y-auto" 
          onScroll={handleScroll}
        >
          {isTerms ? <TermsContent /> : <PrivacyContent />}
          
          {/* Scroll indicator */}
          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              {hasScrolledToBottom ? (
                <span className="flex items-center justify-center gap-1 text-green-500">
                  <CheckCircle className="h-3 w-3" />
                  You've read the {isTerms ? "terms" : "policy"}
                </span>
              ) : (
                "Please scroll to read the entire document"
              )}
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4 flex gap-2 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            data-testid={`${type}-cancel-btn`}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAccept}
            disabled={!hasScrolledToBottom}
            className="bg-gold text-primary-foreground hover:bg-gold/90 disabled:opacity-50"
            data-testid={`${type}-accept-btn`}
          >
            {hasScrolledToBottom ? "I Accept" : "Scroll to Accept"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
